import {FC, Fragment, useCallback, useLayoutEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import styled from "@emotion/styled";
import {useTranslation} from "react-i18next";
import Slider, {Settings} from "react-slick";
import {useIsMobile} from "modules/utils";
import {MAX_VOTE_COUNT} from "modules/constants";
import {getFanVoteHistory, getIsAllVotePicked} from "modules/selectors";
import {Dots} from "components/UserTeamVotes/TeamView/Dots";
import {Exist} from "components";
import {breakpoints} from "assets/theme";
import {HistoryVotePlayer} from "./HistoryVotePlayer";

const Wrapper = styled.div`
	padding: 0 20px;
	display: flex;
	justify-content: center;

	@media (min-width: ${breakpoints.tablet}) {
		margin-top: 40px;
		padding: 0 60px;
	}
`;

const Container = styled.div`
	width: 100%;
	max-width: 1320px;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 20px 10px;
	font-family: ${({theme}) => theme.baseFont};
	color: ${({theme}) => theme.colorPalette.white};
	background: #545456;
	border: 2px solid ${({theme}) => theme.colorPalette.secondary};
	border-radius: 4px;
`;

const Title = styled.div`
	font-size: 20px;
	font-weight: 700;
	line-height: 120%;
	letter-spacing: 0.2px;
	text-transform: uppercase;
	margin-bottom: 10px;

	@media (min-width: ${breakpoints.tablet}) {
		font-size: 32px;
	}
`;

const SubTitle = styled.div`
	font-family: ${({theme}) => theme.secondFont};
	font-size: 14px;
	font-weight: 500;
	line-height: 20px;
	letter-spacing: 0.28px;
	margin-bottom: 10px;

	@media (min-width: ${breakpoints.tablet}) {
		letter-spacing: 0.48px;
		font-size: 24px;
	}
`;

const Content = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 40px;
	width: 100%;
`;

const DotsWrapper = styled.div`
	margin-top: 15px;
	margin-bottom: 20px;

	@media (min-width: ${breakpoints.tablet}) {
		display: none;
	}
`;

const MobileContent = styled.div`
	width: 100%;
	max-width: 282px;
	background: ${({theme}) => theme.colorPalette.black};
`;

export const HistoryVotes: FC = () => {
	const {t} = useTranslation();
	const votes = useSelector(getFanVoteHistory);
	const isAllVotePicked = useSelector(getIsAllVotePicked);
	const [index, setIndex] = useState(0);
	const sliderRef = useRef<Slider>(null);
	const {isMobile} = useIsMobile();

	const settings: Settings = {
		arrows: false,
		dots: false,
		speed: 500,
		infinite: false,
		slidesToShow: 1,
		slidesToScroll: 1,
		beforeChange: (current, next) => setIndex(next),
	};
	const isShowDots = votes.length > 1;

	const changeHandler = useCallback((index: number) => {
		sliderRef.current?.slickGoTo(index);
	}, []);

	useLayoutEffect(() => {
		if (isMobile) {
			setIndex(0);
		}
	}, [isMobile, setIndex]);

	if (!votes.length || isAllVotePicked) {
		return null;
	}

	return (
		<Wrapper>
			<Container>
				<Title>{t("page.vote.my_votes")}</Title>

				<Exist when={isMobile}>
					<SubTitle>
						{t("page.vote.history.vote")} {index + 1} {t("page.vote.history.of")}{" "}
						{MAX_VOTE_COUNT}
					</SubTitle>
				</Exist>

				{isMobile ? (
					<Fragment>
						<MobileContent>
							<Slider ref={sliderRef} {...settings}>
								{votes.map((vote) => (
									<HistoryVotePlayer key={vote.id} {...vote} />
								))}
							</Slider>
						</MobileContent>

						<Exist when={isShowDots}>
							<DotsWrapper>
								<Dots
									length={votes.length}
									index={index}
									changeHandler={changeHandler}
								/>
							</DotsWrapper>
						</Exist>
					</Fragment>
				) : (
					<Content>
						{votes.map((vote) => (
							<HistoryVotePlayer key={vote.id} {...vote} />
						))}
					</Content>
				)}
			</Container>
		</Wrapper>
	);
};
